/**
 * A journal in two columns, as a bookkeeper writes one.
 *
 * The screen used to take ONE amount per line and ask for a minus sign to mean
 * a credit. That is a programmer's encoding of a signed integer, not a
 * bookkeeper's entry: a paper journal has a debit column and a credit column,
 * a line lands in exactly one of them, and the two columns are added
 * separately so the eye can see they agree. This module holds that shape.
 *
 * THE API DOES NOT CHANGE. `postJournal` takes one signed amount per line,
 * positive for a debit, and refuses anything that does not sum to zero
 * (api/src/lib/ledger.js). The two columns are a way of TYPING that number,
 * so the conversion happens here at the edge — `toApiLines` negates the credit
 * column and hands the ledger exactly what it always took.
 *
 * NO parseFloat, AND NO FLOATS AT ALL — the rule web/src/api.js states for the
 * whole client. Adding a column of typed figures for a live total is a display
 * aid, but a display aid that disagrees with the server about a half-cent is
 * worse than none: it would tell a bookkeeper the entry balances and then the
 * post would be refused, or the reverse. Arithmetic here is BigInt over integer
 * cents, so the total this screen shows and the total the ledger computes are
 * the same number arrived at the same way.
 *
 * parseFloat would also be WRONG ABOUT WHAT IS AN AMOUNT: it reads '100 rand'
 * as 100, '1e3' as 1000 and '0x10' as 0, silently. Each of those is somebody
 * mis-keying a figure and being told they succeeded.
 */

/** A fresh, empty line. Two of these are the starting state of the screen. */
export const blankLine = () => ({ accountId: '', debit: '', credit: '', description: '' });

/** The two money columns, in the order they are read. */
export const SIDES = ['debit', 'credit'];

/**
 * Digits, an optional point, at most two decimals. No sign.
 *
 * A minus sign is deliberately NOT accepted. In one column it meant "the other
 * side"; with two columns there is a box for that, and '-100' in the debit
 * column has no single reading — it could be a credit, or a thumb on the wrong
 * key. It is refused by name below rather than quietly treated as either.
 */
const FIGURE = /^(\d*)(?:\.(\d{0,2}))?$/;

export const isBlank = (text) => String(text ?? '').trim() === '';

/**
 * A typed figure as integer cents, or null when it is not a figure.
 *
 * Zero parses — '0.00' is a figure — and is refused later, by `summarise`,
 * because a zero line is a thing the LEDGER refuses (ZERO_LINE) and the
 * refusal belongs where the reason can be given.
 */
export function parseCents(text) {
  const s = String(text ?? '').trim();
  if (s === '') return null;
  const m = FIGURE.exec(s);
  if (!m) return null;
  // '.' alone, or a lone '.5'/'5.' — the first is not a figure; the other two
  // are, and the regex has already split them.
  if (!m[1] && !m[2]) return null;
  return BigInt(m[1] || '0') * 100n + BigInt(((m[2] ?? '') + '00').slice(0, 2));
}

/** Cents back to the decimal string `money()` formats, sign included. */
export function formatCents(cents) {
  const neg = cents < 0n;
  const a = neg ? -cents : cents;
  return `${neg ? '-' : ''}${a / 100n}.${String(a % 100n).padStart(2, '0')}`;
}

/** Which column a line is written in, or null while it is empty. */
export function sideOf(line) {
  if (!isBlank(line?.debit)) return 'debit';
  if (!isBlank(line?.credit)) return 'credit';
  return null;
}

/**
 * Type into one money column, clearing the other.
 *
 * A line is a debit or a credit, never both — that is not a validation rule
 * bolted on afterwards, it is what a line IS. Enforcing it in the state
 * transition rather than at post time means the screen can never hold an entry
 * that is half of each, so there is no case to explain to the user.
 */
export function withAmount(lines, index, side, value) {
  const other = side === 'debit' ? 'credit' : 'debit';
  return lines.map((l, i) => (i === index ? { ...l, [side]: value, [other]: '' } : l));
}

/** The other column is closed while this one has something in it. */
export const isDisabled = (line, side) => {
  const on = sideOf(line);
  return on !== null && on !== side;
};

/**
 * The state of the entry: both column totals, the difference, and whatever
 * stands between it and being postable.
 *
 * Every figure TYPED is counted, whether or not the line has an account yet.
 * The totals have to be what the person sees on the page — a column that
 * silently omits the line they are looking at is a column they cannot check.
 * A figure with no account is reported as a problem instead, because dropping
 * it from the payload is how a screen that says "balanced" earns a 422.
 */
export function summarise(lines) {
  let debitCents = 0n;
  let creditCents = 0n;
  let postable = 0;
  let problem = null;

  const complain = (p) => { if (!problem) problem = p; };

  for (const line of lines ?? []) {
    const side = sideOf(line);
    if (side === null) continue;

    const cents = parseCents(line[side]);
    if (cents === null) {
      complain(String(line[side]).trim().startsWith('-')
        ? 'A line has a minus sign. Put the figure in the other column instead — a credit is a column here, not a negative debit.'
        : 'A line has an amount that is not a figure. Type digits and at most two decimals.');
      continue;
    }
    if (cents === 0n) {
      complain('A line has a zero amount. Every line of a journal has to move something.');
      continue;
    }

    if (side === 'debit') debitCents += cents; else creditCents += cents;
    if (line.accountId) postable += 1;
    else complain('A line has an amount but no account.');
  }

  const differenceCents = debitCents - creditCents;
  return {
    debitCents,
    creditCents,
    differenceCents,
    debit: formatCents(debitCents),
    credit: formatCents(creditCents),
    difference: formatCents(differenceCents),
    // The SAME verdict the ledger reaches, by the same arithmetic: the two
    // columns agree exactly when the signed lines sum to zero.
    balanced: differenceCents === 0n,
    postable,
    problem,
  };
}

/**
 * The lines as the API has always taken them: one signed amount, debit
 * positive. This is the only place the two columns become one number.
 */
export function toApiLines(lines) {
  return (lines ?? []).flatMap((line) => {
    const side = sideOf(line);
    if (side === null || !line.accountId) return [];
    const cents = parseCents(line[side]);
    if (cents === null || cents === 0n) return [];
    return [{
      accountId: line.accountId,
      amount: formatCents(side === 'credit' ? -cents : cents),
      description: line.description || undefined,
    }];
  });
}
